import { create } from 'zustand';
import { buildTreeForPerspective } from '../src/routes/treeBuilder';
import type { Role, UserProfile } from './Types'; // Optional: move types out
import { perspectiveAppRoutes } from '../src/routes/routeConfig';
import { api } from '../src/api/api';
import { accessTokenStore } from '../src/auth/accessTokenStore';
import { getSelectedTenant } from '../src/config/environment';

// Deduplicate logout: only one logout request at a time (prevents dozens of toasts/requests)
let logoutPromise: Promise<void> | null = null;

// Deduplicate refresh: share one in-flight refresh across the proactive useTokenRefresh interval
// and the axios 401 interceptor. Refresh tokens are single-use and rotated server-side, so two
// concurrent refreshes make the loser 401 — and its catch would wipe the valid token the winner
// just stored, logging the user out mid-session. One shared promise prevents that race.
let refreshPromise: Promise<string> | null = null;

type State = {
  userProfile: UserProfile | null;
  accessToken: string | null;
  hydrated: boolean;

  currentPerspective: string;
  treeData: any[];
  selectedNodeId: string | null;
  setSelectedNodeId: (id: string | null) => void;

  login: (tokens: { accessToken: string; skipExpiryCheck?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: () => boolean;
  hasRole: (role: Role) => boolean;
  refreshAccessToken: () => Promise<string>;

  setPerspective: (p: string) => void;
  rebuildTree: () => void;
  /** Re-reads the profile (permissions, licensed modules) and rebuilds the menu, e.g. after a licence upload. */
  reloadProfile: () => Promise<void>;
};

/**
 * Whether the backend rejected the refresh because the session is over (401/403), as opposed to
 * the request never getting an answer. Only the former may clear the client-side session.
 */
export function isAuthRejection(error: unknown): boolean {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return status === 401 || status === 403;
}

export const useStore = create<State>((set, get) => ({
  userProfile: null,
  accessToken: null,
  hydrated: false,

  currentPerspective: 'administration',
  treeData: [],
  selectedNodeId: null,
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),

  
  login: async ({ accessToken, skipExpiryCheck }) => {
    try {
      // CRITICAL: Validate token expiration before using it.
      // In DEV, use timeProvider so we use the same "now" as the backend (mock time); otherwise tokens
      // issued with mock time would be rejected when checked against real Date.now().
      // skipExpiryCheck is used by E2E hydration – the server validates the token anyway via /api/profile.
      if (!skipExpiryCheck) {
        const { isTokenExpiringSoon } = await import('../src/utils/jwt');
        let nowMs: number | undefined;
        if (import.meta.env.DEV) {
          try {
            const { timeProvider } = await import('../src/services/TimeProvider');
            const devNow = await timeProvider.getCurrentTime();
            nowMs = devNow.getTime();
          } catch {
            // fallback: use real time
          }
        }
        if (isTokenExpiringSoon(accessToken, 0, nowMs)) {
          throw new Error('Token is expired or invalid');
        }
      }
      
      accessTokenStore.set(accessToken);

      // CRITICAL: Validate token with backend - this will fail if backend restarted or token invalid
      const res = await api.get<UserProfile>('/api/profile', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.data) throw new Error('Invalid access token');

      const userProfile = res.data;

      // Store tenant ID in session storage for API calls
      if (userProfile.tenant) {
        sessionStorage.setItem('tenantId', userProfile.tenant);
      }

      // Normalize the role to lowercase for consistent comparison
      userProfile.role = userProfile.role.toLowerCase();

      // If superAdmin flag is true or role is superadmin/super_admin, set role to 'superadmin' and give full permissions
      if (userProfile.superAdmin || userProfile.role === 'superadmin' || userProfile.role === 'super_admin') {
        userProfile.role = 'superadmin';
        userProfile.superAdmin = true; // Ensure superAdmin flag is set
        // Give superadmin full permissions for all views
        userProfile.permissions = [{
          viewId: '*',
          read: true,
          write: true
        }];
      }

      // Only check permissions for non-superadmin users
      if (userProfile.role !== 'superadmin') {
        if (!userProfile.permissions || userProfile.permissions.length === 0) {
          console.error('No permissions found for user');
          // Clean up auth state before throwing
          sessionStorage.clear();
          localStorage.removeItem('token');
          set({ userProfile: null, accessToken: null, treeData: [] });
          throw new Error('No permissions assigned. Access denied.');
        }
      }

      const initialTree = buildTreeForPerspective('administration', userProfile, perspectiveAppRoutes);

      // Update state in a single set call to ensure atomic update
      set((state) => ({
        ...state,
        userProfile: userProfile,
        accessToken: accessToken,
        treeData: initialTree,
        hydrated: true,
      }));

      // Force a re-render by updating the state again
      setTimeout(() => {
        set((state) => ({ ...state }));
        // Rebuild tree after state update to ensure it's properly set
        get().rebuildTree();
      }, 0);

    } catch (error: any) {
      // CRITICAL: Clean up auth state on any error (token invalid, backend restart, etc.)
      console.error('❌ Login failed:', error);
      accessTokenStore.clear();
      set({ userProfile: null, accessToken: null, treeData: [] });
      
      // If it's an authentication error (401/403), throw a specific error
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      throw error;
    }
  },

  logout: async () => {
    if (logoutPromise) {
      return logoutPromise;
    }
    logoutPromise = (async () => {
      try {
        await api.post('/auth/logout');
      } catch (error) {
        console.error('Error during logout:', error);
      } finally {
        logoutPromise = null;
        sessionStorage.clear();
        accessTokenStore.clear();
        set({ userProfile: null, accessToken: null, treeData: [] });
      }
    })();
    return logoutPromise;
  },

  isAuthenticated: () => {
    const storeToken = get().accessToken;
    return !!storeToken;
  },
  hasRole: (role: Role) => get().userProfile?.role === role,

  refreshAccessToken: async () => {
    // Single-flight guard: concurrent callers share one in-flight refresh (see refreshPromise
    // declaration). Without it, a losing concurrent refresh would wipe a freshly stored token.
    if (refreshPromise) {
      return refreshPromise;
    }
    refreshPromise = (async (): Promise<string> => {
    // Refresh token is in HTTP-only cookie; api has withCredentials: true so cookie is sent
    const tenantId = sessionStorage.getItem('tenantId') || getSelectedTenant()?.uuid || null;
    if (!tenantId) {
      throw new Error('No tenant ID found');
    }

    try {
      // POST /auth/refresh (AuthControllerService has no refresh in generated code, so call api directly)
      const response = await api.post<{ accessToken: string }>('/auth/refresh', null, {
        headers: { 'X-Tenant-ID': tenantId },
      });
      
      const newAccessToken = response.data?.accessToken;
      if (!newAccessToken) {
        throw new Error('Failed to refresh token - no access token in response');
      }
      
      accessTokenStore.set(newAccessToken);
      set({ accessToken: newAccessToken });

      // Re-fetch profile so that permissions stay in sync after a server restart
      // (RoleSyncService may have added new permissions since the last login).
      try {
        const profileRes = await api.get<UserProfile>('/api/profile', {
          headers: { Authorization: `Bearer ${newAccessToken}` },
        });
        if (profileRes.data) {
          const updatedProfile = profileRes.data;
          updatedProfile.role = updatedProfile.role.toLowerCase();
          if (updatedProfile.superAdmin || updatedProfile.role === 'superadmin' || updatedProfile.role === 'super_admin') {
            updatedProfile.role = 'superadmin';
            updatedProfile.superAdmin = true;
            updatedProfile.permissions = [{ viewId: '*', read: true, write: true }];
          }
          const { currentPerspective } = get();
          const { buildTreeForPerspective } = await import('../src/routes/treeBuilder');
          const { perspectiveAppRoutes } = await import('../src/routes/routeConfig');
          const tree = buildTreeForPerspective(currentPerspective, updatedProfile, perspectiveAppRoutes);
          set({ userProfile: updatedProfile, treeData: tree });
        }
      } catch (profileError) {
        // Profile re-fetch is best-effort; the new access token is already stored.
        console.warn('⚠️ refreshAccessToken: could not re-fetch profile', profileError);
      }

      return newAccessToken;
    } catch (error) {
      console.error('❌ Failed to refresh token:', error);
      // Only an auth rejection ends the session. A transient failure (network blip, backend
      // redeploy, timeout) used to wipe it too, which defeated the fallback in
      // ensureFreshAccessToken: that helper returns the old - often still valid - token so the
      // response path can decide, but by then the state was already gone and PrivateRoute
      // bounced to /login. With the pre-request refresh running inside a one-minute buffer, a
      // single blip logged out a user whose request would have succeeded (review round 5,
      // finding 2.3).
      if (isAuthRejection(error)) {
        sessionStorage.clear();
        accessTokenStore.clear();
        set({ userProfile: null, accessToken: null, treeData: [] });
      }
      throw error;
    }
    })();

    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  },

  setPerspective: (p: string) => {
    const { currentPerspective, userProfile } = get();

    // If perspective hasn't changed, don't rebuild tree
    if (p === currentPerspective) {
      return;
    }

    // If no user profile, set perspective but don't build tree yet
    if (!userProfile) {
      set({ currentPerspective: p, treeData: [] });
      return;
    }

    const tree = buildTreeForPerspective(p, userProfile, perspectiveAppRoutes);
    set({ currentPerspective: p, treeData: tree });
  },

  rebuildTree: () => {
    const { currentPerspective, userProfile } = get();

    if (!userProfile) {
      return;
    }

    const tree = buildTreeForPerspective(currentPerspective, userProfile, perspectiveAppRoutes);
    set({ treeData: tree });
  },

  reloadProfile: async () => {
    const res = await api.get<UserProfile>('/api/profile');
    if (!res.data) {
      return;
    }
    const profile = res.data;
    profile.role = profile.role.toLowerCase();
    if (profile.superAdmin || profile.role === 'superadmin' || profile.role === 'super_admin') {
      profile.role = 'superadmin';
      profile.superAdmin = true;
      profile.permissions = [{ viewId: '*', read: true, write: true }];
    }
    const tree = buildTreeForPerspective(get().currentPerspective, profile, perspectiveAppRoutes);
    set({ userProfile: profile, treeData: tree });
  },
}));
